import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Home, Tent, Flower2, Building2, UtensilsCrossed, Users } from "lucide-react";

const content = {
  sv: {
    title: "Användningsområden för våra kupoler",
    subtitle: "Kupoler anpassade för olika behov och miljöer",
    cases: [
      {
        icon: Home,
        title: "Boende året runt",
        description: "Permanenta och flexibla boendelösningar för året runt-boende i nordiskt klimat.",
        href: "/biokeramiska-domes"
      },
      {
        icon: Tent,
        title: "Glamping & besöksnäring",
        description: "Exklusiva glamping-upplevelser för hotell, campingar och ekoturism.",
        href: "/anvandningsomraden/glamping-kupol"
      },
      {
        icon: Flower2,
        title: "Växthus & uterum",
        description: "Odla året runt eller skapa ett unikt uterum med transparenta kupoler.",
        href: "/anvandningsomraden/kupol-vaxthus"
      },
      {
        icon: UtensilsCrossed,
        title: "Restaurang & event",
        description: "Unika matupplevelser och eventlokaler för restauranger och privata fester.",
        href: "/anvandningsomraden"
      },
      {
        icon: Building2,
        title: "Publika miljöer",
        description: "Innovativa lösningar för offentliga platser, museer och utställningar.",
        href: "/anvandningsomraden"
      },
      {
        icon: Users,
        title: "Studios & communities",
        description: "Kreativa utrymmen för yoga, meditation och co-working.",
        href: "/anvandningsomraden"
      }
    ]
    , readMore: "Läs mer"
  },
  en: {
    title: "Applications for Our Domes",
    subtitle: "Domes adapted for different needs and environments",
    cases: [
      {
        icon: Home,
        title: "Year-round living",
        description: "Permanent and flexible living solutions for year-round living in Nordic climate.",
        href: "/biokeramiska-domes"
      },
      {
        icon: Tent,
        title: "Glamping & tourism",
        description: "Exclusive glamping experiences for hotels, campsites and eco-tourism.",
        href: "/anvandningsomraden/glamping-kupol"
      },
      {
        icon: Flower2,
        title: "Greenhouses & conservatories",
        description: "Grow year-round or create a unique conservatory with transparent domes.",
        href: "/anvandningsomraden/kupol-vaxthus"
      },
      {
        icon: UtensilsCrossed,
        title: "Restaurants & events",
        description: "Unique dining experiences and event venues for restaurants and private parties.",
        href: "/anvandningsomraden"
      },
      {
        icon: Building2,
        title: "Public spaces",
        description: "Innovative solutions for public spaces, museums and exhibitions.",
        href: "/anvandningsomraden"
      },
      {
        icon: Users,
        title: "Studios & communities",
        description: "Creative spaces for yoga, meditation and co-working.",
        href: "/anvandningsomraden"
      }
    ]
    , readMore: "Read more"
  },
  de: {
    title: "Anwendungsbereiche für unsere Kuppeln",
    subtitle: "Kuppeln angepasst an verschiedene Bedürfnisse und Umgebungen",
    cases: [
      {
        icon: Home,
        title: "Ganzjähriges Wohnen",
        description: "Permanente und flexible Wohnlösungen für ganzjähriges Wohnen im nordischen Klima.",
        href: "/biokeramiska-domes"
      },
      {
        icon: Tent,
        title: "Glamping & Tourismus",
        description: "Exklusive Glamping-Erlebnisse für Hotels, Campingplätze und Ökotourismus.",
        href: "/anvandningsomraden/glamping-kupol"
      },
      {
        icon: Flower2,
        title: "Gewächshäuser & Wintergärten",
        description: "Ganzjährig anbauen oder einen einzigartigen Wintergarten mit transparenten Kuppeln schaffen.",
        href: "/anvandningsomraden/kupol-vaxthus"
      },
      {
        icon: UtensilsCrossed,
        title: "Restaurants & Veranstaltungen",
        description: "Einzigartige Esserlebnisse und Veranstaltungsorte für Restaurants und private Feiern.",
        href: "/anvandningsomraden"
      },
      {
        icon: Building2,
        title: "Öffentliche Räume",
        description: "Innovative Lösungen für öffentliche Plätze, Museen und Ausstellungen.",
        href: "/anvandningsomraden"
      },
      {
        icon: Users,
        title: "Studios & Communities",
        description: "Kreative Räume für Yoga, Meditation und Co-Working.",
        href: "/anvandningsomraden"
      }
    ]
    , readMore: "Mehr erfahren"
  }
};

export const UseCases = () => {
  const { language } = useLanguage();
  const t = content[language];

  return (
    <section id="use-cases" className="py-20 px-3 md:px-6 bg-linen">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12 animate-slide-up">
          <h2 className="text-xl sm:text-2xl md:text-4xl font-serif font-bold mb-4 text-foreground break-words">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {t.cases.map((useCase, index) => {
            const IconComponent = useCase.icon;
            return (
              <Link
                key={index}
                to={useCase.href}
                className="group p-6 bg-paper rounded-3xl shadow-paper hover:-translate-y-1 transition-smooth animate-slide-up text-center md:text-left flex flex-col items-center md:items-start"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <IconComponent className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-serif font-semibold mb-2 text-foreground">
                  {useCase.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  {useCase.description}
                </p>
                <span className="mt-auto text-sm font-medium text-primary group-hover:underline">
                  {t.readMore} →
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
